<template>
  <v-card>
    <div class="d-flex flex-row mx-auto py-0 px-0">
      <v-card-text class="text-center card-title font-weight-black pr-0 pb-2 pt-1">
        {{ title }}
      </v-card-text>
      <v-btn icon @click="onExpandClick" class="mr-1" :disabled="!allDemandsAreEqual">
        <v-icon large color="#000000">{{ btnIcon }}</v-icon>
      </v-btn>
    </div>
    <div v-if="eventsForTheDay.length > 0">
      <v-row justify="center" align="center" class="mb-2">
        <v-col cols="auto" class="d-flex flex-wrap ml-2">
          <v-chip
            v-for="event in eventsForTheDay" :key="event.guid"
            color="primary" dark class="ma-1 chip-event">
            {{ event.name }}
          </v-chip>
        </v-col>
      </v-row>
    </div>
    <div v-show="!expanded">
      <dynameat-slider v-if="areGroupsFilled" :ranges="ranges" id="mainSlider"
                       @sliderChanged="onSliderChange" :min="minRange"
                       :max="maxRange" :value="mainSlider" :disabled="disabled"/>
    </div>
    <v-expand-transition>
      <div v-for="(zone, index) in card.zones" :key="index" v-show="expanded">
        <div class="text-center pb-2 mx-3"> {{ zone.zoneNames }}</div>
        <dynameat-slider v-if="areGroupsFilled" :ranges="ranges" :id="zone.saleRateGuid"
                         :min="minRange" :max="maxRange" :value="card.selected[index]"
                         @sliderChanged="onSliderChange" :disabled="disabled"/>
      </div>
    </v-expand-transition>
  </v-card>
</template>
<script>
import DynameatSlider from '@dynameat/components/DynameatSlider';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { Components, MapGetters } from '@core/helpers/decorators';
import { ForecastDemand } from '../models';

@Component
@Components({ DynameatSlider })
@MapGetters(['getSaleZoneNamesByRateGuid', 'groupList'])
export default class ForecastGroupCard extends Vue {
  @Prop({ type: ForecastDemand, required: true }) demand;

  @Prop({ type: Boolean, default: false }) disabled;

  @Prop({ type: String, required: true, default: null }) selectedServiceGuid;

  @Prop({ type: Array, required: false, default: () => [] }) eventOccurrences;

  expanded = false;

  zones = [];

  get btnIcon() {
    return this.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down';
  }

  get title() {
    const [year, month, day] = this.demand.day.split('-');
    const pureDate = new Date(Date.UTC(year, month - 1, day));
    return this.$d(pureDate, 'long');
  }

  get card() {
    const card = {};
    card.zones = [];
    card.selected = [];
    this.demand.sale_rates.forEach((saleRate) => {
      if (saleRate.hasMatchingServices(this.selectedServiceGuid)) {
        const zonesInfo = {
          saleRateGuid: saleRate.guid,
          zoneNames: this.getSaleZoneNamesByRateGuid(saleRate.guid)
        };
        const demandName = saleRate.services.find((service) => service.guid === this.selectedServiceGuid).demand;
        const groupId = this.getGroupId(demandName);
        card.selected.push(groupId);
        card.zones.push(zonesInfo);
      }
    });
    if (card.selected.length === 0) {
      return null;
    }
    return card;
  }

  get eventsForTheDay() {
    return this.eventOccurrences.filter((eventOccurrence) => eventOccurrence.date === this.demand.day);
  }

  get allDemandsAreEqual() {
    return this.card.selected.every((v) => this.card.selected[0] === v);
  }

  get mainSlider() {
    if (this.allDemandsAreEqual) return this.card.selected[0];
    return 0;
  }

  get ranges() {
    return this.groupList.map((group) => group.translation);
  }

  get minRange() {
    return Math.min(...this.groupList.map((group) => group.id));
  }

  get maxRange() {
    return Math.max(...this.groupList.map((group) => group.id));
  }

  get areGroupsFilled() {
    return !!this.groupList.length;
  }

  onExpandClick() {
    this.expanded = !this.expanded;
  }

  getGroupId(groupName) {
    const group = this.groupList.find((dt) => dt.code_name === groupName);
    return group?.id || 4;
  }

  getGroupCodeName(groupId) {
    const group = this.groupList.find((dt) => dt.id === Math.round(groupId));
    return group?.code_name || 'LOW';
  }

  onSliderChange({ id: saleRateGuid, value: groupId }) {
    const groupCodeName = this.getGroupCodeName(groupId);
    if (saleRateGuid === 'mainSlider') {
      const groupInfoList = this.card.zones.map((zonesInfo) => ({
        day: this.demand.day,
        saleRateGuid: zonesInfo.saleRateGuid,
        value: groupCodeName
      }));
      this.$emit('demand-changed', groupInfoList);
    } else {
      this.$emit('demand-changed', [{ day: this.demand.day, saleRateGuid, value: groupCodeName }]);
    }
  }
}
</script>
<style scoped>
.card-title {
  font-size: 1em;
}

.chip-event {
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
  transition: box-shadow 0.3s ease;
}

.chip-event:hover {
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
}

</style>
